import React, { useState } from "react";

// App
import { serverUrl } from "../../config";

// UI components
import { Box, Button, Dialog, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeftOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRightOutlined";

const getEmbedUrl = (url) => {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com")) {
      return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
    }
    if (u.hostname.includes("youtu.be")) {
      return `https://www.youtube.com/embed${u.pathname}`;
    }
    if (u.hostname.includes("vimeo.com")) {
      return `https://player.vimeo.com/video${u.pathname}`;
    }
    return url;
  } catch (e) {
    return url;
  }
};

const sharedFile = (filename, token) =>
  `${serverUrl}/shared/files/${encodeURIComponent(filename)}?access_token=${token}`;

/**
 * Inline dialog viewer for room documents, videos, podcasts, and galleries.
 * Presentations, links, news, and events open in a new tab from the room
 * screen, so they never come here.
 */
export default function RoomItemViewer(props) {
  const { item, onClose, track } = props;
  const [galleryIndex, setGalleryIndex] = useState(0);

  if (!item) {
    return null;
  }

  const sharedFileUrl = item.filename
    ? sharedFile(item.filename, item.file_access_token)
    : null;

  const renderVideo = () => {
    if (item.field_media_oembed_video) {
      return (
        <div className="room-viewer__embed">
          <iframe
            src={getEmbedUrl(item.field_media_oembed_video)}
            title={item.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      );
    }

    if (!sharedFileUrl) {
      return (
        <Typography owner="client" variant="body2">This video is unavailable.</Typography>
      );
    }

    return (
      <video className="room-viewer__video" controls autoPlay>
        <source src={sharedFileUrl} />
        Your browser does not support video playback.
      </video>
    );
  };

  const renderDocument = () => {
    if (!sharedFileUrl) {
      return (
        <Typography owner="client" variant="body2">This document is unavailable.</Typography>
      );
    }

    return (
      <iframe
        className="room-viewer__document"
        src={sharedFileUrl}
        title={item.title}
      ></iframe>
    );
  };

  const renderPodcast = () => {
    if (!sharedFileUrl) {
      return (
        <Typography owner="client" variant="body2">This podcast is unavailable.</Typography>
      );
    }

    return (
      <div className="room-viewer__podcast">
        {item.field_artist && (
          <Typography owner="client" variant="subtitle2" className="room-viewer__artist">
            {item.field_artist}
          </Typography>
        )}
        <audio controls autoPlay className="w-100">
          <source src={sharedFileUrl} />
          Your browser does not support audio playback.
        </audio>
      </div>
    );
  };

  const renderGallery = () => {
    const images = item.images || [];
    if (images.length === 0) {
      return (
        <Typography owner="client" variant="body2">This gallery has no images.</Typography>
      );
    }

    const current = images[Math.min(galleryIndex, images.length - 1)];

    return (
      <div className="room-viewer__gallery">
        <div className="room-viewer__gallery-stage">
          {galleryIndex > 0 && (
            <IconButton
              className="room-viewer__gallery-nav room-viewer__gallery-nav--prev"
              onClick={() => setGalleryIndex(galleryIndex - 1)}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}
          <img
            src={sharedFile(current.filename, current.file_access_token)}
            alt={current.caption || item.title}
          />
          {galleryIndex < images.length - 1 && (
            <IconButton
              className="room-viewer__gallery-nav room-viewer__gallery-nav--next"
              onClick={() => setGalleryIndex(galleryIndex + 1)}
            >
              <ChevronRightIcon />
            </IconButton>
          )}
        </div>
        <div className="room-viewer__gallery-caption">
          <Typography owner="client" variant="caption">
            {current.caption || ""} ({galleryIndex + 1}/{images.length})
          </Typography>
        </div>
        <div className="room-viewer__gallery-thumbs">
          {images.map((image, index) => (
            <button
              type="button"
              key={`thumb-${index}`}
              className={index === galleryIndex ? "active" : ""}
              onClick={() => setGalleryIndex(index)}
            >
              <img
                src={sharedFile(image.filename, image.file_access_token)}
                alt={image.caption || `Image ${index + 1}`}
              />
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderBody = () => {
    switch (item.type) {
      case "video":
        return renderVideo();
      case "podcast":
        return renderPodcast();
      case "gallery":
        return renderGallery();
      default:
        return renderDocument();
    }
  };

  return (
    <Dialog
      className="room-viewer"
      open={true}
      fullWidth
      maxWidth="lg"
      onClose={onClose}
    >
      <Box className="room-viewer__header d-flex align-items-center">
        <Typography owner="client" variant="h6" className="room-viewer__title flex-grow-1">
          {item.title}
        </Typography>
        {item.type === "document" && sharedFileUrl && (
          <Button
            size="small"
            variant="outlined"
            className="buyer-room__btn buyer-room__btn--outlined"
            startIcon={<DownloadIcon />}
            component="a"
            href={sharedFileUrl}
            download={item.filename}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              track({
                event_type: "content_downloaded",
                content_type: item.type,
                content_id: Number(item.id),
              });
            }}
          >
            <Typography owner="client" variant="button">
              Download
            </Typography>
          </Button>
        )}
        <IconButton onClick={onClose} title="Close">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box className="room-viewer__body">{renderBody()}</Box>
    </Dialog>
  );
}
